import { Type, Sun, Contrast, Keyboard, ALargeSmall } from 'lucide-react';
import { useUIPrefsStore } from '@renderer/store/useUIPrefsStore';
import {
  ESCALA_DEFAULT,
  ESCALA_MAX,
  ESCALA_MIN,
  ESCALA_PUNTOS,
  ESCALA_STEP,
  FuenteFamilia,
  FUENTE_FAMILIA_DESCRIPCION,
  FUENTE_FAMILIA_LABEL,
  FUENTE_FAMILIA_STACK,
  TemaUI,
  TEMA_LABEL,
  escalaLabel,
  factorEscala,
} from '@renderer/utils/uiPrefs';

/**
 * Sprint 40 — Switcher de accesibilidad: escala de fuente + tema.
 * Sprint 48 — Agrega slider continuo de tamaño + selector de familia de fuente.
 */
export function AparienciaForm() {
  const escala = useUIPrefsStore((s) => s.escala);
  const tema = useUIPrefsStore((s) => s.tema);
  const fuente = useUIPrefsStore((s) => s.fuente);
  const setEscala = useUIPrefsStore((s) => s.setEscala);
  const setTema = useUIPrefsStore((s) => s.setTema);
  const setFuente = useUIPrefsStore((s) => s.setFuente);
  const errorGuardado = useUIPrefsStore((s) => s.errorGuardado);

  const factor = factorEscala(escala);
  const pct = Math.round(factor * 100);

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Sprint 56.3 — Si el cambio se ve pero no se guarda, hay que decirlo:
          antes el usuario sólo lo descubría al reiniciar. */}
      {errorGuardado && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          Se ve el cambio, pero no se pudo guardar: al reiniciar volverá al tamaño anterior.
        </div>
      )}

      {/* ============== Tamaño de letra — slider ============== */}
      <section className="card space-y-4">
        <header className="flex items-center gap-2">
          <Type className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Tamaño de la letra</h2>
        </header>
        <p className="text-sm text-slate-600">
          Mueve el deslizador hasta que el texto se vea cómodo en tu pantalla.
          Los cambios aplican al instante a toda la aplicación.
        </p>

        {/* Slider */}
        <div className="space-y-2 px-1">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Tamaño:</span>
            <span className="font-bold text-primary text-base tabular-nums">
              {escalaLabel(escala)}&nbsp;·&nbsp;{pct}%
            </span>
          </div>

          <input
            type="range"
            min={ESCALA_MIN}
            max={ESCALA_MAX}
            step={ESCALA_STEP}
            value={factor}
            onChange={(e) => void setEscala(e.target.value)}
            className="w-full h-2 cursor-pointer accent-primary"
            aria-label="Tamaño de letra"
          />

          {/* Etiquetas de referencia */}
          <div className="flex justify-between text-[11px] text-slate-400 select-none">
            {ESCALA_PUNTOS.map((p) => (
              <button
                key={p.factor}
                type="button"
                onClick={() => void setEscala(String(p.factor))}
                className={`transition-colors hover:text-primary ${
                  Math.abs(factor - p.factor) < 0.001
                    ? 'text-primary font-semibold'
                    : ''
                }`}
                title={`Ir a ${p.label} (${Math.round(p.factor * 100)}%)`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Preview en vivo — la fuente y tamaño cambian al instante */}
        <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-4 space-y-1">
          <div className="text-xs uppercase tracking-widest text-slate-600 mb-2">
            Vista previa
          </div>
          <div className="font-bold">Espadas Cerdo Mix — $500.00</div>
          <div className="text-slate-600">Mesa #3 · Comer aquí · 2 comensales</div>
          <div className="text-sm text-slate-500 mt-1">
            Lomo, costilla y longaniza. Stock: 8 unidades.
          </div>
        </div>

        {/* Atajos de teclado */}
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-start gap-2 text-xs text-slate-600">
          <Keyboard className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>Atajos:</strong>{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-300 font-mono text-[11px]">Ctrl</kbd>
            {' '}<kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-300 font-mono text-[11px]">+</kbd>
            {' '}para subir ·{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-300 font-mono text-[11px]">Ctrl</kbd>
            {' '}<kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-300 font-mono text-[11px]">-</kbd>
            {' '}para bajar ·{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-300 font-mono text-[11px]">Ctrl</kbd>
            {' '}<kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-300 font-mono text-[11px]">0</kbd>
            {' '}para volver a Normal (100%).
          </div>
        </div>

        {/* Botón de reset rápido si no está en 100% */}
        {Math.abs(factor - 1.0) > 0.001 && (
          <button
            type="button"
            onClick={() => void setEscala(ESCALA_DEFAULT)}
            className="btn-ghost text-xs text-slate-500"
          >
            Restaurar a Normal (100%)
          </button>
        )}
      </section>

      {/* ============== Tipo de letra ============== */}
      <section className="card space-y-3">
        <header className="flex items-center gap-2">
          <ALargeSmall className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Tipo de letra</h2>
        </header>
        <p className="text-sm text-slate-600">
          <strong>Calibri</strong> y <strong>Segoe UI</strong> tienen caracteres más estrechos que Inter,
          lo que ayuda a mostrar más contenido en pantallas pequeñas.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {(['inter', 'segoe', 'calibri'] as FuenteFamilia[]).map((f) => {
            const activa = fuente === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFuente(f)}
                className={`flex min-h-[100px] flex-col items-center justify-center gap-1.5 rounded-xl border-2 p-3 text-center transition ${
                  activa
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                }`}
                aria-pressed={activa}
              >
                <span
                  className="text-3xl font-bold leading-none tracking-tight"
                  style={{ fontFamily: FUENTE_FAMILIA_STACK[f] }}
                >
                  Aa
                </span>
                <span className="text-sm font-semibold">{FUENTE_FAMILIA_LABEL[f]}</span>
                <span className="text-xs text-slate-500 leading-tight">{FUENTE_FAMILIA_DESCRIPCION[f]}</span>
              </button>
            );
          })}
        </div>

        <p className="text-xs text-slate-500">
          Fuentes del sistema Windows — sin conexión a internet. Si <em>Calibri</em> no se ve diferente,
          usa <em>Segoe UI</em> en su lugar.
        </p>
      </section>

      {/* ============== Tema ============== */}
      <section className="card space-y-3">
        <header className="flex items-center gap-2">
          <Contrast className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Estilo visual</h2>
        </header>
        <p className="text-sm text-slate-600">
          Si los bordes y la separación entre tarjetas se ven difusos, prueba
          el <strong>alto contraste</strong>.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <TarjetaOpcion<TemaUI>
            valor="claro"
            seleccionada={tema}
            onSeleccionar={setTema}
            titulo={TEMA_LABEL.claro}
            descripcion="Bordes suaves"
            icono={<Sun className="h-7 w-7" />}
          />
          <TarjetaOpcion<TemaUI>
            valor="alto-contraste"
            seleccionada={tema}
            onSeleccionar={setTema}
            titulo={TEMA_LABEL['alto-contraste']}
            descripcion="Bordes marcados y texto más oscuro"
            icono={<Contrast className="h-7 w-7" />}
          />
        </div>
      </section>
    </div>
  );
}

interface TarjetaOpcionProps<T extends string> {
  valor: T;
  seleccionada: T;
  onSeleccionar: (v: T) => void;
  titulo: string;
  descripcion: string;
  preview?: string;
  previewSize?: string;
  icono?: React.ReactNode;
}

function TarjetaOpcion<T extends string>({
  valor,
  seleccionada,
  onSeleccionar,
  titulo,
  descripcion,
  preview,
  previewSize,
  icono,
}: TarjetaOpcionProps<T>) {
  const activa = valor === seleccionada;
  return (
    <button
      type="button"
      onClick={() => onSeleccionar(valor)}
      className={`flex min-h-[100px] flex-col items-center justify-center gap-1 rounded-xl border-2 p-3 text-center transition ${
        activa
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
      }`}
      aria-pressed={activa}
    >
      {preview && (
        <div className={`font-bold leading-none ${previewSize ?? 'text-2xl'}`}>
          {preview}
        </div>
      )}
      {icono && <div className="text-slate-700">{icono}</div>}
      <div className="font-semibold text-sm">{titulo}</div>
      <div className="text-xs text-slate-500">{descripcion}</div>
    </button>
  );
}
